/*
  One Tooth World — cart (PRD §6.7). Loaded globally as a module.
  OTW.cart.add(variantId, qty, button) / addMany(items, button) / change(key, qty) / remove(key) / refresh() / open() / close()
  Re-renders the drawer from the Section Rendering API ('otw-cart-drawer') on every change.
  Also wires the global [data-otw-add-to-cart] and [data-otw-quick-view] buttons (quick view loads lazily).
*/
const OTW = (window.OTW = window.OTW || {});
const SECTION = 'otw-cart-drawer';
const t = (key, fallback) => (OTW.strings && OTW.strings[key]) || fallback;
const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const drawer = () => document.getElementById('OtwCartDrawer');

const upsellCache = new Map();
let queue = Promise.resolve();

function toastError(message) {
  OTW.toast?.(message || t('cartError', 'We could not update your cart.'), { icon: false });
}

async function post(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status) {
    const error = new Error(data.description || data.message || 'Cart error');
    error.description = data.description || data.message;
    throw error;
  }
  return data;
}

/* Swap the drawer contents with the freshly rendered section */
function swapContent(current, html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const next = doc.querySelector('[data-otw-cart-content]');
  if (!next) return null;
  const hadFocusOn = current.contains(document.activeElement) ? document.activeElement.closest?.('[data-otw-line]')?.dataset.key : null;
  const scroller = current.querySelector('.otw-cart__body');
  const scrollTop = scroller ? scroller.scrollTop : 0;
  current.className = next.className;
  Object.keys(next.dataset).forEach((k) => (current.dataset[k] = next.dataset[k]));
  current.innerHTML = next.innerHTML;
  const body = current.querySelector('.otw-cart__body');
  if (body && scrollTop) body.scrollTop = scrollTop;
  if (hadFocusOn) current.querySelector(`[data-key="${CSS.escape(hadFocusOn)}"] [data-otw-qty]`)?.focus({ preventScroll: true });
  return { count: Number(next.dataset.cartCount) || 0, total: Number(next.dataset.cartTotal) || 0 };
}

/* `sections` is the Section Rendering API map. Every [data-otw-cart-content] (drawer, cart page) is swapped. */
function render(sections) {
  if (!sections) return null;
  let state = null;
  document.querySelectorAll('[data-otw-cart-content]').forEach((current) => {
    const html = sections[current.dataset.otwCartSection || SECTION];
    if (!html) return;
    const result = swapContent(current, html);
    if (result) state = state || result;
  });
  if (state) loadUpsell();
  return state;
}

function applyState(state, extra = {}) {
  if (!state) return;
  OTW.updateBubbles?.('cart', state.count);
  OTW.emit('cart:updated', { item_count: state.count, total_price: state.total, cart: { item_count: state.count, total_price: state.total }, ...extra });
}

async function loadUpsell() {
  const els = [...document.querySelectorAll('[data-otw-cart-upsell]')];
  const id = els[0] && els[0].dataset.productId;
  if (!id) return;
  const fill = (html) =>
    document.querySelectorAll('[data-otw-cart-upsell]').forEach((el) => {
      if (el.dataset.productId === id) el.innerHTML = html;
    });
  if (upsellCache.has(id)) {
    fill(upsellCache.get(id));
    return;
  }
  try {
    const response = await fetch(`${root()}recommendations/products?product_id=${id}&limit=6&section_id=otw-cart-upsell&intent=related`);
    if (!response.ok) return;
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const html = (doc.querySelector('.shopify-section') || doc.body).innerHTML.trim();
    upsellCache.set(id, html);
    fill(html);
  } catch {
    /* upsell is optional */
  }
}

/* The cart page section is re-rendered alongside the drawer when it is on the page */
const sectionNames = () => (document.querySelector('[data-otw-cart-section="otw-main-cart"]') ? `${SECTION},otw-main-cart` : SECTION);
const sectionParams = () => ({ sections: sectionNames(), sections_url: window.location.pathname });

OTW.cart = {
  open(opener) {
    const el = drawer();
    if (el && el.open) el.open(opener);
    else window.location.href = window.routes?.cart_url || '/cart';
  },
  close() {
    drawer()?.close?.();
  },

  async addMany(items, button, { open = true } = {}) {
    if (button?.getAttribute('aria-busy') === 'true') return false;
    OTW.setButtonState?.(button, 'loading');
    try {
      const data = await post(`${root()}cart/add.js`, { items, ...sectionParams() });
      const state = render(data.sections);
      if (state) applyState(state, { items: data.items });
      else await OTW.cart.refresh();
      if (button) {
        OTW.setButtonState?.(button, 'success');
        setTimeout(() => OTW.setButtonState?.(button, 'reset'), 1500);
      }
      if (open) OTW.cart.open(button);
      return true;
    } catch (error) {
      OTW.setButtonState?.(button, 'reset');
      toastError(error.description);
      return false;
    }
  },

  add(variantId, qty = 1, button, options) {
    return OTW.cart.addMany([{ id: Number(variantId), quantity: Number(qty) || 1 }], button, options);
  },

  change(key, qty) {
    // Serialise requests so quick taps never race
    queue = queue.then(async () => {
      try {
        const cart = await post(`${root()}cart/change.js`, { id: key, quantity: Math.max(0, qty), ...sectionParams() });
        const state = render(cart.sections);
        applyState(state || { count: cart.item_count, total: cart.total_price });
      } catch (error) {
        toastError(error.description || t('cartError', ''));
        await OTW.cart.refresh();
      }
    });
    return queue;
  },

  remove(key) {
    return OTW.cart.change(key, 0);
  },

  async refresh() {
    try {
      const response = await fetch(`${root()}?sections=${sectionNames()}`, { headers: { Accept: 'application/json' } });
      const data = await response.json();
      applyState(render(data));
    } catch {
      /* keep the current drawer */
    }
  },
};

/* ---------- Optimistic quantity updates ---------- */
function updateTotalsOptimistically(content) {
  let total = 0;
  let count = 0;
  content.querySelectorAll('[data-otw-line]').forEach((line) => {
    const qty = Number(line.querySelector('[data-otw-qty]').value) || 0;
    const lineTotal = Number(line.dataset.unitPrice) * qty;
    total += lineTotal;
    count += qty;
    const out = line.querySelector('[data-otw-line-total]');
    if (out) out.textContent = OTW.money(lineTotal);
  });
  const subtotal = content.querySelector('[data-otw-subtotal]');
  if (subtotal) subtotal.textContent = OTW.money(total);
  const threshold = Number(content.dataset.threshold) || 0;
  if (threshold) {
    const text = content.querySelector('[data-otw-ship-text]');
    const fill = content.querySelector('[data-otw-ship-fill]');
    const remaining = threshold - total;
    if (fill) fill.style.width = `${Math.min(100, Math.floor((total / threshold) * 100))}%`;
    if (text) {
      text.textContent =
        remaining > 0
          ? t('cartAway', "You're [amount] away from free delivery").replace('[amount]', OTW.money(remaining))
          : t('cartFree', "You've unlocked free delivery!");
    }
  }
  OTW.updateBubbles?.('cart', count);
}

const timers = new Map();

function setQty(line, qty) {
  const input = line.querySelector('[data-otw-qty]');
  qty = Math.max(0, Math.floor(Number(qty)) || 0);
  input.value = qty;
  const content = line.closest('[data-otw-cart-content]');
  if (qty === 0) line.classList.add('is-removing');
  updateTotalsOptimistically(content);
  clearTimeout(timers.get(line.dataset.key));
  timers.set(
    line.dataset.key,
    setTimeout(() => OTW.cart.change(line.dataset.key, qty), qty === 0 ? 180 : 350)
  );
}

document.addEventListener('click', async (event) => {
  const target = event.target;

  const add = target.closest('[data-otw-add-to-cart]');
  if (add) {
    event.preventDefault();
    OTW.cart.add(add.dataset.variantId, 1, add);
    return;
  }

  const qv = target.closest('[data-otw-quick-view]');
  if (qv) {
    event.preventDefault();
    OTW.quickView.open(qv.dataset.otwQuickView, qv);
    return;
  }

  const line = target.closest('[data-otw-line]');
  if (line) {
    const input = line.querySelector('[data-otw-qty]');
    if (target.closest('[data-otw-qty-minus]')) setQty(line, Number(input.value) - 1);
    else if (target.closest('[data-otw-qty-plus]')) setQty(line, Number(input.value) + 1);
    else if (target.closest('[data-otw-remove]')) setQty(line, 0);
    else if (target.closest('[data-otw-move-wishlist]')) {
      const handle = target.closest('[data-otw-move-wishlist]').dataset.otwMoveWishlist;
      if (OTW.wishlist && !OTW.wishlist.has(handle)) OTW.wishlist.add(handle);
      setQty(line, 0);
    }
    return;
  }

  const toggle = target.closest('[data-otw-note-toggle]');
  if (toggle) {
    const field = document.getElementById(toggle.getAttribute('aria-controls'));
    const open = toggle.getAttribute('aria-expanded') !== 'true';
    toggle.setAttribute('aria-expanded', String(open));
    if (field) {
      field.hidden = !open;
      if (open) field.querySelector('textarea')?.focus();
    }
  }
});

document.addEventListener('change', (event) => {
  const input = event.target.closest?.('[data-otw-qty]');
  if (input) {
    setQty(input.closest('[data-otw-line]'), input.value);
    return;
  }
  const note = event.target.closest?.('[data-otw-note]');
  if (note) {
    post(`${root()}cart/update.js`, { note: note.value }).catch((error) => toastError(error.description));
  }
});

OTW.on?.('cart:open', ({ opener } = {}) => OTW.cart.open(opener));

/* ---------- Quick view: lazy-loaded on first use ---------- */
let quickViewModule;
OTW.quickView = OTW.quickView || {
  async open(handle, opener) {
    try {
      quickViewModule = quickViewModule || import(OTW.assets.quickView);
      await quickViewModule;
      return OTW.quickView.impl.open(handle, opener);
    } catch {
      quickViewModule = null;
      window.location.href = `${root()}products/${handle}`;
    }
  },
};

/* Seed the count from the server-rendered drawer */
const initial = document.querySelector('[data-otw-cart-content]');
if (initial && OTW.counts && OTW.counts.cart === undefined) OTW.counts.cart = Number(initial.dataset.cartCount) || 0;
OTW.emit?.('cart:ready');
